import { IsOptional, IsArray, IsUUID } from 'class-validator'
import { ApiProperty, ApiPropertyOptional } from '@nestjs/swagger'
/**
 * DTO pour la validation atomique d'une assignation teaching
 * Envoyé à School pour valider tous les critères en une seule passe
 */
export class ValidateTeachingAssignmentRequestDto {
  @ApiPropertyOptional({
    type: [String],
    description: 'Liste des UUIDs de leçons à valider',
    example: ['660e8400-e29b-41d4-a716-446655440001'],
  })
  @IsOptional()
  @IsArray()
  @IsUUID('4', { each: true })
  lessonIds?: string[]

  @ApiProperty({
    type: 'string',
    description: "UUID de l'utilisateur (enseignant)",
    example: '550e8400-e29b-41d4-a716-446655440000',
  })
  @IsUUID()
  userId!: string

  @ApiPropertyOptional({
    type: 'string',
    description: "UUID de la matière de l'activité (pour vérification cohérence)",
    example: '770e8400-e29b-41d4-a716-446655440002',
  })
  @IsOptional()
  @IsUUID()
  activitySubjectExternalId?: string

  @ApiPropertyOptional({
    type: 'string',
    description: "UUID du niveau de l'activité (pour vérification cohérence)",
    example: '880e8400-e29b-41d4-a716-446655440003',
  })
  @IsOptional()
  @IsUUID()
  activityLevelExternalId?: string

  @ApiPropertyOptional({
    type: 'string',
    description: "UUID de l'établissement de l'activité (pour vérification cohérence)",
    example: '990e8400-e29b-41d4-a716-446655440004',
  })
  @IsOptional()
  @IsUUID()
  activityEstablishmentId?: string
}

/**
 * Erreur de validation retournée par School
 */
export interface ValidationTeachingAssignmentError {
  code: ValidationTeachingAssignmentErrorCode
  message: string
  lessonId?: string
  field?: string
}

/**
 * Codes d'erreur standardisés pour la validation
 */
export enum ValidationTeachingAssignmentErrorCode {
  // Staff/Access
  NOT_STAFF = 'NOT_STAFF',
  STAFF_PERIOD_INVALID = 'STAFF_PERIOD_INVALID',

  // Teaching
  TEACHING_NOT_FOUND = 'TEACHING_NOT_FOUND',
  TEACHING_DELETED = 'TEACHING_DELETED',
  TEACHING_INACTIVE = 'TEACHING_INACTIVE',

  // Lessons
  INVALID_LESSON = 'INVALID_LESSON',
  LESSON_NOT_IN_TEACHING = 'LESSON_NOT_IN_TEACHING',
  LESSON_DELETED = 'LESSON_DELETED',

  // School Year
  SCHOOL_YEAR_INACTIVE = 'SCHOOL_YEAR_INACTIVE',
  SCHOOL_YEAR_ARCHIVED = 'SCHOOL_YEAR_ARCHIVED',
  SCHOOL_YEAR_NOT_STARTED = 'SCHOOL_YEAR_NOT_STARTED',

  // Coherence
  SUBJECT_MISMATCH = 'SUBJECT_MISMATCH',
  LEVEL_MISMATCH = 'LEVEL_MISMATCH',
  ESTABLISHMENT_MISMATCH = 'ESTABLISHMENT_MISMATCH',
}

/**
 * Contexte teaching retourné par School pour dénormalisation
 */
export interface TeachingContext {
  id: string
  subjectExternalId: string
  subjectLabel: string
  levelExternalId: string
  levelLabel: string
  establishmentId: string
  establishmentLabel?: string
  schoolYearId: string
  schoolYearCode: string
  schoolYearStatus: 'ACTIVE' | 'ARCHIVED' | 'FUTURE'
  classId?: string
  groupId?: string
}

/**
 * Réponse de validation atomique retournée par School
 */
export class ValidateTeachingAssignmentResponseDto {
  @ApiProperty({
    type: Boolean,
    description: "Indique si l'assignation peut être effectuée",
    example: true,
  })
  canAssign!: boolean

  @ApiProperty({
    type: [Object],
    description: 'Liste des erreurs de validation',
    example: [],
  })
  errors!: ValidationTeachingAssignmentError[]

  @ApiPropertyOptional({
    type: Object,
    description: 'Contexte teaching si validation réussie',
  })
  teaching?: TeachingContext
}

/**
 * Type guard to check if a validation response is valid
 */
export function isValidTeachingValidation(
  validation: ValidateTeachingAssignmentResponseDto
): validation is ValidateTeachingAssignmentResponseDto & {
  teaching: TeachingContext
} {
  return validation.canAssign && validation.teaching !== undefined
}
